import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import "@/landing.css";
import { Wordmark, GREEN, BLUE, ORANGE } from "@/components/landing/shared";
import { FooterLegal } from "@/components/public/FooterLegal";

const NAV = [
  ["VSME", "/vsme"], ["PMI-Banche", "/pmi-banche"], ["Environmental", "/environmental"],
  ["Social", "/social"], ["Governance", "/governance"], ["Carbon Calculator", "/carbon-calculator"],
  ["JOBTIMEHR", "/jobtimehr"], ["Eva", "/eva"], ["Per le PMI", "/pmi"],
  ["Consulenti", "/consulenti"], ["Guide", "/guide"], ["Chi siamo", "/chi-siamo"],
];
const NAV_MAIN = ["/vsme", "/pmi-banche", "/carbon-calculator", "/jobtimehr", "/eva", "/guide"];

export function PublicHeader() {
  const [open, setOpen] = useState(false);
  const main = NAV.filter(([, href]) => NAV_MAIN.includes(href));
  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-200" data-testid="public-header">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" aria-label="ESG-PMI home"><Wordmark compact /></Link>
        <nav className="hidden lg:flex items-center gap-5" aria-label="Navigazione principale">
          {main.map(([label, href]) => (
            <Link key={href} to={href}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              data-testid={`pubnav-${href.slice(1)}`}>
              {label}
            </Link>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/login" className="text-sm font-semibold px-4 py-2 rounded-md" style={{ color: BLUE }}
            data-testid="pubnav-login">Accedi</Link>
          <Link to="/richiedi-demo"
            className="text-sm font-bold px-5 py-2.5 rounded-md text-white transition-transform hover:-translate-y-0.5"
            style={{ background: ORANGE }} data-testid="pubnav-demo">
            Richiedi una demo
          </Link>
        </div>
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)}
          aria-label={open ? "Chiudi menu" : "Apri menu"} aria-expanded={open}
          data-testid="pubnav-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-slate-200 bg-white px-5 py-4 space-y-1" aria-label="Menu mobile">
          {NAV.map(([label, href]) => (
            <Link key={href} to={href} onClick={() => setOpen(false)}
              className="block py-2.5 text-sm font-medium text-slate-700"
              data-testid={`pubnav-mobile-${href.slice(1)}`}>
              {label}
            </Link>
          ))}
          <div className="flex gap-3 pt-3 border-t border-slate-100">
            <Link to="/login" className="flex-1 text-center text-sm font-semibold py-2.5 rounded-md border border-slate-300"
              style={{ color: BLUE }} data-testid="pubnav-login-mobile">Accedi</Link>
            <Link to="/richiedi-demo" className="flex-1 text-center text-sm font-bold py-2.5 rounded-md text-white"
              style={{ background: ORANGE }} data-testid="pubnav-demo-mobile">Richiedi demo</Link>
          </div>
        </nav>
      )}
    </header>
  );
}

export function PublicFooter() {
  const cols = [
    { t: "Prodotto", links: [["VSME", "/vsme"], ["PMI-Banche", "/pmi-banche"], ["Environmental", "/environmental"], ["Social", "/social"], ["Governance", "/governance"], ["Carbon Calculator", "/carbon-calculator"], ["Cassetto ESG", "/cassetto-esg"]] },
    { t: "Piattaforma", links: [["Eva", "/eva"], ["Integrazione JOBTIMEHR", "/jobtimehr"]] },
    { t: "Soluzioni", links: [["Per le PMI", "/pmi"], ["Per i consulenti", "/consulenti"]] },
    { t: "Azienda", links: [["Chi siamo", "/chi-siamo"], ["Guide", "/guide"], ["Richiedi una demo", "/richiedi-demo"], ["Privacy", "/privacy"], ["Termini", "/termini"]] },
  ];
  return (
    <footer style={{ background: "#0C2540" }} data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
        <div className="grid md:grid-cols-5 gap-10">
          <div>
            <span className="inline-block bg-white rounded-lg px-3 py-1.5"><Wordmark compact /></span>
            <p className="mt-3 text-xs text-white/60 leading-relaxed">
              La piattaforma ESG per micro, piccole e medie imprese. Integrata con JOBTIMEHR.
            </p>
          </div>
          {cols.map(({ t, links }) => (
            <nav key={t} aria-label={`Footer ${t}`}>
              <p className="text-xs font-bold uppercase tracking-wider text-white/50 mb-3">{t}</p>
              <ul className="space-y-2">
                {links.map(([label, href]) => (
                  <li key={href}>
                    <Link to={href} className="text-sm text-white/75 hover:text-white transition-colors"
                      data-testid={`footer-${href.slice(1) || "home"}`}>{label}</Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
        <FooterLegal />
      </div>
    </footer>
  );
}

export default function PublicLayout({ children, breadcrumb = [] }) {
  return (
    <div className="bg-white text-slate-900 antialiased" style={{ fontFamily: "Inter, sans-serif" }}
      data-testid="public-layout">
      <PublicHeader />
      {breadcrumb.length > 0 && (
        <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto px-5 lg:px-8 pt-5"
          data-testid="public-breadcrumb">
          <ol className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500 list-none p-0">
            <li><Link to="/" className="hover:text-slate-800 font-medium">Home</Link></li>
            {breadcrumb.map((b) => (
              <li key={b.path} className="flex items-center gap-1.5">
                <span aria-hidden="true">›</span>
                <span className="font-semibold text-slate-700" aria-current="page">{b.name}</span>
              </li>
            ))}
          </ol>
        </nav>
      )}
      <main>{children}</main>
      <PublicFooter />
    </div>
  );
}
